/** localStorage key indicating the user has been granted access to the v2 front. */
export const V2_ACCESS_KEY = 'msys_v2_access';

/** localStorage key storing the user's v2 toggle preference. */
export const V2_TOGGLE_KEY = 'msys_v2_toggle';

/**
 * Map of migrated v1 route paths to v2 path builder functions.
 * Keys are v1 route patterns; values are functions(params) => string.
 * Only routes listed here will trigger a v2 redirect in onEnter.
 */
export const MIGRATED_PAGES = {
  '/dashboard/follow-ups': () => '/v2/follow-ups',
  '/dashboard/clients/:clientId/members/:memberId/profile':
    (params) => `/v2/clients/${params.clientId}/members/${params.memberId}/profile`,
  '/dashboard/clients/:clientId/members/:memberId/coverage':
    (params) => `/v2/clients/${params.clientId}/members/${params.memberId}/coverage`,
  '/dashboard/clients/:clientId/members/:memberId/beneficiaries':
    (params) => `/v2/clients/${params.clientId}/members/${params.memberId}/beneficiaries`,
  '/dashboard/clients/:clientId/members/:memberId/dependents':
    (params) => `/v2/clients/${params.clientId}/members/${params.memberId}/dependents`,
  '/dashboard/clients/:clientId/members/:memberId/employments':
    (params) => `/v2/clients/${params.clientId}/members/${params.memberId}/employments`,
};

/**
 * Returns the v2 URL for the given pathname if it matches a migrated page, or null otherwise.
 * Converts MIGRATED_PAGES route patterns (e.g. /dashboard/clients/:clientId/...)
 * into regexes, extracts URL params, and calls the corresponding v2PathFn.
 *
 * @param {string} pathname - e.g. window.location.pathname
 * @returns {string|null} v2 URL string, or null if not a migrated page
 */
export const getV2RedirectForCurrentPath = (pathname) => {
  for (const [pattern, v2PathFn] of Object.entries(MIGRATED_PAGES)) {
    const paramNames = [];
    const regexStr = pattern.replace(/:([^/]+)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    });
    const match = pathname.match(new RegExp(`^${regexStr}(/.*)?$`));
    if (match) {
      const params = {};
      paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });
      return v2PathFn(params);
    }
  }
  return null;
};

/** @returns {boolean} Whether the current user has been granted v2 access via Keycloak attribute. */
export const hasV2Access = () => localStorage.getItem(V2_ACCESS_KEY) === 'true';

/** @returns {boolean} Whether the user has the v2 toggle enabled. */
export const isV2ToggleOn = () => localStorage.getItem(V2_TOGGLE_KEY) === 'true';

/**
 * Persists the v2 toggle preference.
 * @param {boolean} value
 */
export const setV2ToggleOn = (value) => localStorage.setItem(V2_TOGGLE_KEY, String(value));

/**
 * Stores or clears the v2 access flag derived from the Keycloak token claim msys-www-v2.
 * @param {boolean} value
 */
export const setV2Access = (value) => {
  if (value) {
    localStorage.setItem(V2_ACCESS_KEY, 'true');
  } else {
    localStorage.removeItem(V2_ACCESS_KEY);
  }
};

/** Clears all v2-related localStorage keys. Call on logout. */
export const clearV2Storage = () => {
  localStorage.removeItem(V2_ACCESS_KEY);
  localStorage.removeItem(V2_TOGGLE_KEY);
};

/**
 * Higher-order function that wraps a React Router v2 onEnter guard.
 * If the user has v2 access, toggle is ON, and a v2 path function is provided,
 * performs a full-page redirect to the v2 equivalent and short-circuits the guard.
 * Otherwise delegates to the original guard (or calls callback directly if none provided).
 *
 * @param {Function|null} v2PathFn - function(params) => string that builds the v2 URL
 * @param {Function|null} originalOnEnter - existing onEnter guard to delegate to when not redirecting
 * @returns {Function} React Router v2 onEnter-compatible async function
 */
export const withV2Redirect = (v2PathFn, originalOnEnter = null) =>
  async (nextState, replace, callback) => {
    if (hasV2Access() && isV2ToggleOn() && v2PathFn) {
      window.location.href = v2PathFn(nextState.params);
      return; // no callback — full page navigation in progress
    }
    if (originalOnEnter) {
      return originalOnEnter(nextState, replace, callback);
    }
    callback();
  };



// WEBPACK FOOTER //
// ./src/utils/v2Access.js